// src/hooks/useSubscriptionLocale.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useAuthStore } from 'stores/auth';
import { useThemeStore } from 'stores/theme';
import { safeAppLocale, safeAppLanguage, FALLBACK_LOCALE } from 'utils/localeUtils';
import { getDateFormatByLocale } from 'utils/helpers';
import app from 'configs/appResolver';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const FORMAT_CONFIG = {
  'pt-br': { thousandSeparator: '.', decimalSeparator: ',', defaultCurrency: 'BRL' },
  'pt-pt': { thousandSeparator: '.', decimalSeparator: ',', defaultCurrency: 'EUR' },
  'en-us': { thousandSeparator: ',', decimalSeparator: '.', defaultCurrency: 'USD' },
  'en-gb': { thousandSeparator: ',', decimalSeparator: '.', defaultCurrency: 'GBP' },
  'en-au': { thousandSeparator: ',', decimalSeparator: '.', defaultCurrency: 'AUD' },
  'en-ca': { thousandSeparator: ',', decimalSeparator: '.', defaultCurrency: 'CAD' },
  'de-de': { thousandSeparator: '.', decimalSeparator: ',', defaultCurrency: 'EUR' },
  'fr-fr': { thousandSeparator: ' ', decimalSeparator: ',', defaultCurrency: 'EUR' },
  'es-es': { thousandSeparator: '.', decimalSeparator: ',', defaultCurrency: 'EUR' },
  'it-it': { thousandSeparator: '.', decimalSeparator: ',', defaultCurrency: 'EUR' },
};

const DEFAULT_FORMAT = { thousandSeparator: ',', decimalSeparator: '.', defaultCurrency: 'USD' };

// *******************************************************************************
// HOOK
// *******************************************************************************
const useSubscriptionLocale = () => {
  const storedCountryCode = useAuthStore(s => s.subscription?.countryCode);
  const storedLanguage = useThemeStore(s => s.language);

  // Priority: subscription.countryCode -> user selected language -> app.LOCALE -> fallback
  const localeKey = storedCountryCode
    ? safeAppLocale(storedCountryCode)
    : safeAppLanguage(storedLanguage || app.LOCALE || FALLBACK_LOCALE);
  const config = FORMAT_CONFIG[localeKey] || DEFAULT_FORMAT;

  // BCP47 form: 'pt-br' -> 'pt-BR', 'en-us' -> 'en-US'
  const locale = localeKey
    .split('-')
    .map((p, i) => (i === 0 ? p : p.toUpperCase()))
    .join('-');

  return {
    locale,
    localeKey,
    thousandSeparator: config.thousandSeparator,
    decimalSeparator: config.decimalSeparator,
    dateFormat: getDateFormatByLocale(localeKey),
    defaultCurrency: config.defaultCurrency,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useSubscriptionLocale;
